import { useMemo, useState } from "react";
import { Paperclip, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import ReceiptViewer from "@/components/dashboard/ReceiptViewer";
import { fmtMoney, fmtShortDate, groupByMonth, DATE_RANGES, inputCls, cardCls } from "./accountingUtils";

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "largest", label: "Largest" },
  { key: "smallest", label: "Smallest" },
];

const selCls = `${inputCls} bg-[#0a0a0a] h-9 w-auto min-w-0`;

/**
 * AccountingExpenses — the Expenses list: filter bar, month groups with
 * subtotals, tap a row to edit, paperclip opens the receipt.
 */
const AccountingExpenses = ({ base, expenses, loading, filters, setFilters, readOnly, onEdit, onChanged }) => {
  const confirm = useConfirm();
  const [receiptSrc, setReceiptSrc] = useState(null);
  const [opening, setOpening] = useState(null);

  const categories = useMemo(() => {
    const s = new Set();
    expenses.forEach((e) => (e.lines || []).forEach((l) => l.category && s.add(l.category)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  const groups = useMemo(() => groupByMonth(expenses), [expenses]);
  const grand = expenses.reduce((s, e) => s + (Number(e.total) || 0), 0);

  const openReceipt = async (e) => {
    setOpening(e.expense_id);
    try {
      const { data } = await api.get(`${base}/expenses/${e.expense_id}`);
      if (!data.receipt) return toast.error("No receipt on this expense");
      setReceiptSrc(data.receipt);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open receipt");
    } finally {
      setOpening(null);
    }
  };

  const remove = async (e) => {
    const ok = await confirm({
      title: "Delete this expense?",
      description: `${e.payee || e.category || "Expense"} · ${fmtMoney(e.total)} on ${fmtShortDate(e.date)}. This cannot be undone.`,
      confirmLabel: "Delete expense",
    });
    if (!ok) return;
    try {
      await api.delete(`${base}/expenses/${e.expense_id}`);
      toast.success("Expense deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const f = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-3" data-testid="acct-expenses">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-40">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input value={filters.q} onChange={(e) => f("q", e.target.value)} placeholder="Search" data-testid="acct-search" className={`${inputCls} h-9 pl-8`} />
        </div>
        <select value={filters.sort} onChange={(e) => f("sort", e.target.value)} data-testid="acct-sort" className={selCls}>
          {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
        </select>
        <select value={filters.range} onChange={(e) => f("range", e.target.value)} data-testid="acct-range" className={selCls}>
          {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => f("category", e.target.value)} data-testid="acct-category" className={selCls}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className={`${cardCls} flex items-center justify-between`} data-testid="acct-expenses-total">
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">{expenses.length} expense{expenses.length === 1 ? "" : "s"}</span>
        <span className="font-mono text-lg font-bold text-white">{fmtMoney(grand)}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className={`${cardCls} py-10 text-center text-sm text-white/50`} data-testid="acct-expenses-empty">
          No expenses yet{readOnly ? "" : " — tap + to add your first one"}.
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="overflow-hidden rounded-lg border border-white/10" data-testid={`acct-month-${g.key}`}>
            <div className="flex items-center justify-between bg-white/[0.06] px-4 py-2">
              <span className="font-mono text-[11px] uppercase tracking-widest text-white/70">{g.label}</span>
              <span className="font-mono text-sm font-semibold text-white">{fmtMoney(g.total)}</span>
            </div>
            <ul className="divide-y divide-white/10 bg-white/[0.02]">
              {g.items.map((e) => (
                <li key={e.expense_id} className="flex items-center gap-3 px-4 py-3" data-testid={`acct-expense-${e.expense_id}`}>
                  <button type="button" onClick={() => !readOnly && onEdit(e)} className="min-w-0 flex-1 text-left" data-testid={`acct-expense-${e.expense_id}-open`}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{fmtShortDate(e.date)}{e.ref_no ? ` · #${e.ref_no}` : ""}</div>
                    <div className="truncate text-sm font-semibold text-white">{e.payee || e.category || "Expense"}</div>
                    <div className="truncate text-xs text-white/55">
                      {e.category}{e.lines?.[0]?.who_for ? ` · ${e.lines[0].who_for}` : ""}{e.lines?.length === 1 && e.lines[0].description ? ` · ${e.lines[0].description}` : ""}
                    </div>
                  </button>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="font-mono text-sm font-semibold text-white">{fmtMoney(e.total)}</span>
                    {e.has_receipt && (
                      <button type="button" onClick={() => openReceipt(e)} data-testid={`acct-expense-${e.expense_id}-receipt`} aria-label="View receipt" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white">
                        {opening === e.expense_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                      </button>
                    )}
                    {!readOnly && (
                      <>
                        <button type="button" onClick={() => onEdit(e)} data-testid={`acct-expense-${e.expense_id}-edit`} aria-label="Edit" className="hidden rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-white sm:block"><Pencil className="h-4 w-4" /></button>
                        <button type="button" onClick={() => remove(e)} data-testid={`acct-expense-${e.expense_id}-delete`} aria-label="Delete" className="rounded-md p-1.5 text-white/60 hover:bg-white/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}

      <ReceiptViewer src={receiptSrc} onClose={() => setReceiptSrc(null)} />
    </div>
  );
};

export default AccountingExpenses;
